import { UploadOutlined, CopyOutlined } from "@ant-design/icons";
import {
  Modal,
  Form,
  Input,
  Upload,
  Button,
  List,
  Typography,
  Divider,
  Tooltip,
  message
} from "antd";
import axios from "axios";
import { useEffect, useState } from "react";

import SlugFormItem from "src/components/CourseContentManagement/ModulesManagement/SlugFormItem";
import { copyTextToClipboard } from "src/utils/clipboardHelper";
import { showErrorNotification } from "src/utils/notification";

const { Title } = Typography;
const { Item } = Form;

const CreateEditModuleModal = ({
  open,
  onCancel = () => {},
  onCreate = () => {},
  onUpdate = () => {},
  initialValues = {},
  loading,
  ROOT_URL,
  BASE_URL
}) => {
  const [form] = Form.useForm();
  const isEdit = !!initialValues && Object.keys(initialValues).length > 0;

  const [assets, setAssets] = useState([]);
  const [uploadingAsset, setUploadingAsset] = useState(false);

  const copyToClipboard = async (text) => {
    const hasCopied = await copyTextToClipboard(text);
    if (!hasCopied) {
      message.error("Failed to copy URL");
      return;
    }
    message.success("URL copied to clipboard!");
  };

  const uploadAssets = async (file) => {
    setUploadingAsset(true);
    const formData = new FormData();
    formData.append("file", file);

    const uniquePublicId = initialValues?.uniquePublicId;
    formData.append("uniquePublicId", uniquePublicId);

    try {
      const res = await axios.post(`${ROOT_URL}/${BASE_URL}/upload-asset`, formData);
      const url = res.data.url;
      setAssets((prev) => [...prev, { name: file.name, url }]);
      message.success(`${file.name} uploaded successfully.`);
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setUploadingAsset(false);
    }
  };

  const beforeUpload = (file) => {
    uploadAssets(file);
    return false; // prevent default upload
  };

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (initialValues && Object.keys(initialValues).length > 0) {
        form.setFieldsValue(initialValues);
      }
      setAssets([]); // reset assets when opened
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form]);

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        if (isEdit) {
          onUpdate(values);
        } else {
          onCreate(values);
        }
      })
      .catch((info) => {
        console.error("Validate Failed:", info);
      });
  };

  return (
    <Modal
      open={open}
      title={isEdit ? "Edit Module" : "Create New Module"}
      okText={isEdit ? "Update" : "Create"}
      cancelText="Cancel"
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
    >
      <Form
        form={form}
        layout="vertical"
        name="form_in_modal"
        initialValues={{ modifier: "public" }}
      >
        <Item
          name="title"
          label="Title"
          rules={[
            {
              required: true,
              message: "Please input the title of the module!"
            }
          ]}
        >
          <Input />
        </Item>
        <SlugFormItem
          isEdit={isEdit}
          entityName="module"
          reservedSlugs={initialValues?.reservedSlugs}
        />

        <Item name="bannerImageUrl" label="Banner Image URL">
          <Input placeholder="Enter banner image URL" disabled={!isEdit} />
        </Item>

        <Item name="moduleIconImageUrl" label="Module Icon Image URL">
          <Input placeholder="Enter module icon image URL" disabled={!isEdit} />
        </Item>

        <Divider />
        <Title level={4}>Assets / Files</Title>
        <div style={{ marginBottom: 12 }}>
          <Upload beforeUpload={beforeUpload} multiple showUploadList={false} disabled={!isEdit}>
            <Button icon={<UploadOutlined />} loading={uploadingAsset} disabled={!isEdit}>
              Upload Asset
            </Button>
          </Upload>

          {!isEdit && (
            <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              Please create the module first to upload assets.
            </Typography.Text>
          )}

          <div style={{ marginTop: 12 }}>
            <List
              bordered
              dataSource={assets}
              locale={{ emptyText: "No assets uploaded yet." }}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Tooltip title="Copy URL" key="copy">
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(item.url)}
                      />
                    </Tooltip>
                  ]}
                >
                  <Typography.Text copyable={{ text: item.url }} ellipsis style={{ width: "80%" }}>
                    {item.name}
                  </Typography.Text>
                </List.Item>
              )}
            />
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateEditModuleModal;
